
import Vue from "vue";
import {SlimSelectOption} from "src/components/common/vue-slim-select/vue-slim-select";
import ImageSizeComponent from "./image-size.vue";
import {ImageSize} from "../../resource/image-resource";

export type ResizeBase = "original" | "width" | "height";

export interface PickedImageSize {
  base: ResizeBase;
  side?: number;
  format?: string;
}

export interface SupportedFormat {
  key: string,
  name: string,
  description: string,
}

export default Vue.extend({
  name: "image-size-picker",
  components: {
    "image-size": ImageSizeComponent,
  },
  props: {
    sizes: {
      type: Array,
      required: true,
    },
    formats: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
  data() {
    return {
      base: "original" as ResizeBase,
      side: 24 as number | string | undefined,
      format: "auto" as string,
      editor: "hidden" as "hidden" | "edit" | "create",
      editorIndex: -1 as number,
      resizeBases: ["original", "width", "height"] as ResizeBase[],
    }
  },
  computed: {
    pickedSizes(): PickedImageSize[] {
      const sizes = this.sizes as PickedImageSize[] | undefined;
      if (sizes && Array.isArray(sizes)) {
        return sizes;
      } else {
        return [{ base: "original", format: (this.formats as SupportedFormat[])[0].key }]
      }
    },

    sideAsNumber(): undefined | number {
      const side = this.side;
      if (typeof side === "number") {
        if (Number.isSafeInteger(side)) {
          return side;
        }
      } else if (typeof side === "string") {
        try {
          const parsed = Number.parseInt(side, 10);
          if (Number.isSafeInteger(parsed)) {
            this.side = parsed;
            return parsed;
          }
        } catch (e) {
          // Ignore
        }
      }
      return undefined;
    },

    editorError(): string | undefined {
      const sizes = this.pickedSizes;
      if (this.base === "original" && sizes.some((it, i) => it.base === 'original' && (this.editor !== 'edit' || i !== this.editorIndex))) {
        return "Can't have multiple sizes with 'original' size."
      }
      const side = this.sideAsNumber;
      if (this.base === "width" || this.base === "height") {
        if (side) {
          if (side <= 0) {
            return "Specify a valid image size";
          }
          if (side > 5000) {
            return "Pick a smaller size";
          }
        } else {
          return "Must specify the desired size"
        }
      }
      for (let i = 0; i < sizes.length; i++) {
        const size = sizes[i];
        if ((this.editor !== 'edit' || i !== this.editorIndex) && this.base === size.base && Math.round(side || 0) === Math.round(size.side || 0)) {
          if (this.format === 'auto' || size.format === 'auto') {
            return "Can't have the same size multiple times if the format is set to auto.";
          }
          if (this.format === size.format) {
            return `The specified size and format is already present.`;
          }
        }
      }
    },
  },
  mounted() {
  },
  methods: {
    generateSingleSelectOptions: function(): SlimSelectOption[] {
      const slimSelectOptions: SlimSelectOption[] = [];
      for (let format of (this.formats as SupportedFormat[])) {
        slimSelectOptions.push({
          value: format.key,
          text: format.description,
          selected: this.format === format.key,
        })
      }
      return slimSelectOptions;
    },
    startAddingNewSize() {
      if (!this.editable) return;
      const sizes = this.pickedSizes;
      this.base = sizes.some(it => it.base === "original") ? "width" : "original";
      this.side = 24;
      this.format = "auto";
      this.editor = "create";
    },
    startEditingSize(index: number) {
      if (!this.editable) return;
      const size = this.pickedSizes[index];
      if (size) {
        this.base = size.base;
        this.side = size.side || 24;
        this.format = size.format || "auto";
        this.editor = "edit";
        this.editorIndex = index;
      } else {
        console.log(`Can't edit size index: ${index}`);
        this.startAddingNewSize();
      }
    },
    saveSize() {
      if (!this.editorError) {
        const size: PickedImageSize = {
          base: this.base,
          side: this.base === "original" ? undefined : this.sideAsNumber,
          format: this.format,
        }
        let sizes: PickedImageSize[] | undefined = undefined;
        if (this.editor === "create") {
          sizes = [...this.pickedSizes, size]
        } else if (this.editorIndex >= 0 && this.editorIndex < this.pickedSizes.length) {
          sizes = [...this.pickedSizes];
          sizes[this.editorIndex] = size;
        }
        if (sizes) {
          this.editor = "hidden";
          this.editorIndex = -1;
          this.$emit("on-sizes-changed", sizes);
        }
      }
    },
    removeSize(index: number) {
      const sizes = this.pickedSizes;
      if (index >= 0 && sizes.length > index) {
        sizes.splice(index, 1);
        this.editor = "hidden";
        this.$emit("on-sizes-changed", sizes);
      }
      return true;
    },
    findFormat(format: string): SupportedFormat {
      const existing = (this.formats as SupportedFormat[]).find(it => it.key === format);
      if (existing) {
        return existing;
      } else {
        return { name: `${format}`, description: "Unknown format", key: format };
      }
    }
  }
})

export function convertImageSizesToPickedSizes(sizes: ImageSize[]): PickedImageSize[] {
  return sizes.map(size => {
    const side = size.type === "width" ? size.width : (size.type === 'height' ? size.height : undefined)
    return {
      base: size.type == null ? "original" : size.type,
      side: side == null ? undefined : side,
      format: size.format == null ? "auto" : size.format,
    }
  });
}

export function convertPickedSizesToImageSizes(sizes: PickedImageSize[]): ImageSize[] {
  return sizes.map(size => {
    return {
      type: size.base,
      width: size.base === 'width' ? size.side : undefined,
      height: size.base === 'height' ? size.side : undefined,
      format: size.format,
    }
  });
}

